import { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { _t, _tParts } from '@/i18n/t';
import { assistantProviderLabel, type AssistantProvider } from '../utils/assistantProviders';

export interface AssistantsData {
  quote?: string;
  queries?: string[];
  activeProvider?: AssistantProvider;
  providerCounts: Partial<Record<AssistantProvider, number>>;
  providerResults?: Record<string, AssistantProvider[]>;
}

interface Props {
  data: AssistantsData;
  /** The pass has settled, so no further query or answer is coming. */
  complete?: boolean;
  /** Fired once, when every assistant has answered every query this panel knows about. */
  onPlaybackComplete?: () => void;
}

// Only these two are actually asked — the citation checker queries no others,
// so the rest are listed with a status that says so rather than sitting on
// "waiting" for a turn that never comes.
const QUERIED_PROVIDERS: AssistantProvider[] = ['chatgpt', 'claude'];

const CHIP_SETTLE_MS = 3000;
const QUERY_ADVANCE_MS = 2500;

export default function AssistantsPanel({ data, complete = false, onPlaybackComplete }: Props) {
  const { quote, queries, providerResults } = data;

  const knownQueries = queries ?? [];
  const [displayCount, setDisplayCount] = useState(1);
  const shownQueries = knownQueries.slice(0, Math.max(1, Math.min(displayCount, knownQueries.length)));
  const currentQuery = shownQueries[shownQueries.length - 1] ?? quote;
  const isLiveQuery = shownQueries.length >= knownQueries.length;

  const recorded = currentQuery ? providerResults?.[currentQuery] : undefined;
  const settledOrder: AssistantProvider[] = recorded ?? (isLiveQuery && !complete ? [] : QUERIED_PROVIDERS);

  // The count is stored against the query it belongs to, so moving to the next
  // question resets it in the same render rather than one render later.
  const [chipProgress, setChipProgress] = useState<{ query?: string; green: number }>({ query: currentQuery, green: 0 });
  const greenCount = chipProgress.query === currentQuery ? chipProgress.green : 0;

  useEffect(() => {
    if (greenCount >= settledOrder.length) return;
    const id = setTimeout(() => setChipProgress({ query: currentQuery, green: greenCount + 1 }), CHIP_SETTLE_MS);
    return () => clearTimeout(id);
  }, [greenCount, settledOrder.length, currentQuery]);

  // Every assistant that answered has been shown. Counting against the full
  // list instead would wait on one that never reports a result.
  const allGreen = settledOrder.length > 0 && greenCount >= settledOrder.length;

  useEffect(() => {
    if (!allGreen || displayCount >= knownQueries.length) return;
    const id = setTimeout(() => setDisplayCount(c => c + 1), QUERY_ADVANCE_MS);
    return () => clearTimeout(id);
  }, [allGreen, displayCount, knownQueries.length]);

  const lastAnswered = knownQueries.length > 0 && displayCount >= knownQueries.length && allGreen;
  const [lastHeld, setLastHeld] = useState(false);
  useEffect(() => {
    if (!lastAnswered) return;
    const id = setTimeout(() => setLastHeld(true), QUERY_ADVANCE_MS);
    return () => clearTimeout(id);
  }, [lastAnswered]);

  const playbackDone = complete
    && displayCount >= knownQueries.length
    && (knownQueries.length === 0 || lastHeld);
  const notifiedRef = useRef(false);
  useEffect(() => {
    if (!playbackDone || notifiedRef.current) return;
    notifiedRef.current = true;
    onPlaybackComplete?.();
  }, [playbackDone, onPlaybackComplete]);

  const greenProviders = settledOrder.slice(0, greenCount);
  const active = QUERIED_PROVIDERS.find(p => !greenProviders.includes(p));

  return (
    <div className="ob-chat" data-testid="onboarding-scan-assistants">
      {currentQuery && (
        <div className="ob-chat__q text-muted-foreground" key={currentQuery} data-testid="onboarding-scan-assistant-query">
          {currentQuery}
        </div>
      )}

      <ul className="ob-chat__rows bg-card">
        {QUERIED_PROVIDERS.map(provider => {
          const isDone = greenProviders.includes(provider);
          const isActive = provider === active && !!currentQuery;
          return (
            <li
              key={provider}
              data-testid={`onboarding-scan-assistant-${provider}`}
              className={cn('ob-arow', isActive && 'ob-arow--active', isDone && 'ob-arow--done')}
            >
              <span className="ob-arow__name">{assistantProviderLabel(provider)}</span>
              {isActive ? (
                <span className="ob-arow__dots" aria-label={_t('onboarding.crawl.chapter.assistants.answering')}>
                  <i /><i /><i />
                </span>
              ) : (
                <span className="ob-arow__state text-muted-foreground">
                  {isDone && <Check aria-hidden="true" />}
                  {isDone
                    ? _t('onboarding.crawl.chapter.assistants.answered')
                    : _t('onboarding.crawl.chapter.assistants.waiting')}
                </span>
              )}
            </li>
          );
        })}
      </ul>

      <div className="ob-chat__status text-[hsl(var(--text-muted))]" aria-live="polite">
        {active && currentQuery
          ? _tParts('onboarding.crawl.chapter.assistants.asking', { provider: assistantProviderLabel(active) }).map((part, i) =>
              typeof part === 'string' ? part : <b key={i} className="ob-chat__provider text-muted-foreground">{part.value}</b>,
            )
          : _t('onboarding.crawl.chapter.assistants.scoring')}
      </div>
    </div>
  );
}
